<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling-ownership.UI.3 / PASS.1 / PASS.2 — the dock's "Pass call
// to…" picker: search, teammates with their status (busy / offline / chats
// only are shown but disabled), a note for the teammate (≤ 280), "Also
// assign the conversation to <name>" and the two modes, Talk first / Pass
// now. The call keeps running while the picker is open.
import { computed, ref, watch } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { PASS_MODE, PASS_NOTE_MAX as NOTE_MAX } from './kaptionCallModel';

const props = defineProps({
  targets: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
  customerName: { type: String, default: '' },
  elapsed: { type: String, default: '' },
});

const emit = defineEmits(['pass', 'close']);

const query = ref('');
const selectedId = ref(null);
const note = ref('');
const assignConversation = ref(true);

const STATUS_KEYS = {
  busy: 'KAPTION_CALLS.PASS.STATUS_BUSY',
  offline: 'KAPTION_CALLS.PASS.STATUS_OFFLINE',
  chats_only: 'KAPTION_CALLS.PASS.STATUS_CHATS_ONLY',
};

const statusKey = target =>
  target.selectable
    ? 'KAPTION_CALLS.PASS.STATUS_AVAILABLE'
    : STATUS_KEYS[target.reason] || 'KAPTION_CALLS.PASS.STATUS_OFFLINE';

const statusClass = target => {
  if (target.selectable) return 'text-n-teal-11';
  if (target.reason === 'busy') return 'text-n-amber-11';
  return 'text-n-slate-11';
};

const filtered = computed(() => {
  const term = query.value.trim().toLowerCase();
  const list = term
    ? props.targets.filter(t => (t.name || '').toLowerCase().includes(term))
    : props.targets;
  // Selectable first, then by name.
  return [...list].sort(
    (a, b) =>
      Number(!!b.selectable) - Number(!!a.selectable) ||
      String(a.name).localeCompare(String(b.name))
  );
});

const selected = computed(
  () => props.targets.find(t => t.id === selectedId.value) || null
);
const firstName = computed(
  () => (selected.value?.name || '').split(' ')[0] || ''
);

// Preselect the first teammate who can take it.
watch(
  () => props.targets,
  targets => {
    if (selected.value?.selectable) return;
    selectedId.value = targets.find(t => t.selectable)?.id ?? null;
  },
  { immediate: true }
);

const choose = target => {
  if (target.selectable) selectedId.value = target.id;
};

const submit = mode => {
  if (!selected.value?.selectable || props.busy) return;
  emit('pass', {
    toUserId: selected.value.id,
    mode,
    note: note.value.trim().slice(0, NOTE_MAX),
    assignConversation: assignConversation.value,
  });
};
</script>

<template>
  <div
    data-testid="kaption-pass-picker"
    class="flex flex-col gap-3 px-4 pb-3 pt-1"
  >
    <div class="flex items-center justify-between gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('KAPTION_CALLS.PASS.TITLE') }}
      </span>
      <span class="flex items-center gap-2 text-xs text-n-slate-11">
        <span class="truncate">{{ customerName }}</span>
        <span v-if="elapsed" class="tabular-nums">· {{ elapsed }}</span>
        <Button
          data-testid="kaption-pass-close"
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="xs"
          :aria-label="$t('KAPTION_CALLS.PASS.CLOSE')"
          @click="emit('close')"
        />
      </span>
    </div>

    <label
      class="flex h-8 items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-black2 px-2"
    >
      <span class="i-lucide-search size-3.5 text-n-slate-11" />
      <input
        v-model="query"
        data-testid="kaption-pass-search"
        type="search"
        class="!mb-0 h-full flex-1 border-0 bg-transparent !p-0 text-sm text-n-slate-12 outline-none"
        :placeholder="$t('KAPTION_CALLS.PASS.SEARCH')"
      />
    </label>

    <div
      role="radiogroup"
      class="flex max-h-44 flex-col gap-1 overflow-y-auto"
      :aria-label="$t('KAPTION_CALLS.PASS.TITLE')"
    >
      <p v-if="loading" class="px-2 py-1.5 text-xs text-n-slate-11">
        {{ $t('KAPTION_CALLS.PASS.LOADING') }}
      </p>
      <p
        v-else-if="!filtered.length"
        data-testid="kaption-pass-empty"
        class="px-2 py-1.5 text-xs text-n-slate-11"
      >
        {{ $t('KAPTION_CALLS.PASS.EMPTY') }}
      </p>
      <button
        v-for="target in filtered"
        :key="target.id"
        type="button"
        role="radio"
        :data-testid="`kaption-pass-target-${target.id}`"
        :aria-checked="selectedId === target.id"
        :disabled="!target.selectable"
        class="flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left disabled:cursor-not-allowed disabled:opacity-60"
        :class="
          selectedId === target.id
            ? 'border-n-teal-8 bg-n-teal-3'
            : 'border-n-weak hover:bg-n-alpha-2'
        "
        @click="choose(target)"
      >
        <Avatar
          :name="target.name"
          :src="target.avatar_url"
          :size="24"
          rounded-full
        />
        <span class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-sm text-n-slate-12">{{
            target.name
          }}</span>
          <span class="truncate text-xs" :class="statusClass(target)">
            {{ $t(statusKey(target)) }}
          </span>
        </span>
        <span
          class="size-4 shrink-0 rounded-full border"
          :class="
            selectedId === target.id
              ? 'border-n-teal-9 bg-n-teal-9'
              : 'border-n-weak'
          "
        />
      </button>
    </div>

    <label v-if="selected" class="flex flex-col gap-1">
      <span class="text-xs text-n-slate-11">
        {{ $t('KAPTION_CALLS.PASS.NOTE_FOR', { name: firstName }) }}
      </span>
      <textarea
        v-model="note"
        data-testid="kaption-pass-note"
        rows="2"
        :maxlength="NOTE_MAX"
        class="!mb-0 resize-none rounded-lg border border-n-weak bg-n-alpha-black2 px-2 py-1.5 text-sm text-n-slate-12"
        :placeholder="$t('KAPTION_CALLS.PASS.NOTE_PLACEHOLDER')"
      />
    </label>

    <label
      v-if="selected"
      class="flex items-center gap-2 text-xs text-n-slate-12"
    >
      <input
        v-model="assignConversation"
        data-testid="kaption-pass-assign"
        type="checkbox"
        class="!mb-0"
      />
      {{ $t('KAPTION_CALLS.PASS.ASSIGN', { name: firstName }) }}
    </label>

    <div class="grid grid-cols-2 gap-2">
      <Button
        data-testid="kaption-pass-talk-first"
        :label="$t('KAPTION_CALLS.PASS.TALK_FIRST')"
        color="slate"
        variant="faded"
        size="sm"
        :disabled="!selected?.selectable || busy"
        @click="submit(PASS_MODE.TALK_FIRST)"
      />
      <Button
        data-testid="kaption-pass-now"
        :label="$t('KAPTION_CALLS.PASS.PASS_NOW')"
        size="sm"
        :is-loading="busy"
        :disabled="!selected?.selectable || busy"
        @click="submit(PASS_MODE.PASS_NOW)"
      />
    </div>
    <p class="text-[11px] leading-4 text-n-slate-11">
      {{
        $t('KAPTION_CALLS.PASS.HINT', {
          customer: customerName,
          name: firstName || $t('KAPTION_CALLS.PASS.THEM'),
        })
      }}
    </p>
  </div>
</template>
